/**
 * SplitText
 * 要素内のテキストを1文字ずつ <span class="char"> で囲う（文字送りアニメ用）。
 *
 * - **テキストノードのみ**を分割対象にする。`<br>` や `<span class="cl-xxx">`（部分カラー）等の
 *   既存タグは壊さずそのまま温存する（要素ノードは再帰して中のテキストだけ分割）。
 * - 各 char には `--char-index`（0始まりの通し番号）を付与。CSSの transition-delay 等で
 *   段差（stagger）アニメーションを作るために使う。
 * - サロゲートペア（絵文字等）は for...of で1コードポイント単位に分割。空白は `.char-space` を付与。
 * - 多重実行ガード：処理済み要素には data-split-done を付け、再分割しない。
 * - 文字送りの見た目は要素の data属性で調整可（→CSSカスタムプロパティ。CSS側は var(…,既定) 参照）：
 *     data-split-distance / data-split-duration / data-split-stagger / data-split-delay（applyConfig 参照）
 *
 * @example
 * HTML: <p class="p-kv-title js-split-text" data-split-distance="0.1em" data-split-stagger="0.03s">
 *         あ<span class="cl-brand">い</span><br>う
 *       </p>
 * JS:   new SplitText();                       // 既定 .js-split-text
 *       new SplitText({ selector: '.foo' });
 */
export default class SplitText {
  constructor(options = {}) {
    this.selector = options.selector || '.js-split-text';
    this.charClass = options.charClass || 'char';

    document.querySelectorAll(this.selector).forEach((el) => this.split(el));
  }

  /**
   * 1要素を文字分割する
   * @param {HTMLElement} root
   */
  split(root) {
    if (!root || root.dataset.splitDone === 'true') return;

    this.applyConfig(root);

    const counter = { i: 0 };
    this.walk(root, counter);

    root.dataset.splitDone = 'true';
    root.classList.add('is-split');
  }

  /**
   * data属性 → CSSカスタムプロパティへ反映（CSS側は var(…, 既定値) で参照）。
   * 文字送りの見た目をマークアップ側で自由に調整できるようにする。
   *   data-split-distance : 立ち上がりの移動量（数値のみなら em／例 0.1em・6px・0）  → --char-move
   *   data-split-duration : 1文字のフェード時間（数値のみなら s／例 0.5s・300ms）     → --char-duration
   *   data-split-stagger  : 1文字ごとの遅延差（数値のみなら s／例 0.03s）             → --char-stagger
   *   data-split-delay    : 出現の開始までの基準遅延（数値のみなら s／例 0.2s）       → --char-base
   *   data-split-out-delay: フェードアウト開始の遅延（数値のみなら s／例 0.5s）        → --char-out-delay
   * @param {HTMLElement} root
   */
  applyConfig(root) {
    const len = (v) => (/^-?[\d.]+$/.test(v) ? `${v}em` : v); // 単位なし数値は em
    const time = (v) => (/^[\d.]+$/.test(v) ? `${v}s` : v);   // 単位なし数値は s

    const ds = root.dataset;
    if (ds.splitDistance != null && ds.splitDistance !== '') root.style.setProperty('--char-move', len(ds.splitDistance));
    if (ds.splitDuration != null && ds.splitDuration !== '') root.style.setProperty('--char-duration', time(ds.splitDuration));
    if (ds.splitStagger != null && ds.splitStagger !== '') root.style.setProperty('--char-stagger', time(ds.splitStagger));
    if (ds.splitDelay != null && ds.splitDelay !== '') root.style.setProperty('--char-base', time(ds.splitDelay));
    if (ds.splitOutDelay != null && ds.splitOutDelay !== '') root.style.setProperty('--char-out-delay', time(ds.splitOutDelay));
  }

  /**
   * 子ノードを走査し、テキストノードだけ char span へ置換。要素ノードは温存して再帰。
   * @param {Node} node
   * @param {{i:number}} counter - 全体で連番を振るためのカウンタ（参照渡し）
   */
  walk(node, counter) {
    // 走査中にDOMを差し替えるので、子の静的コピーを取ってから回す
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        this.replaceTextNode(child, counter);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        // <br> 等の改行や、既に分割済みの char はそのまま温存
        if (child.tagName === 'BR') return;
        if (child.classList && child.classList.contains(this.charClass)) return;
        // 色指定 span 等のインライン要素は「要素は残して」中のテキストだけ分割
        this.walk(child, counter);
      }
    });
  }

  /**
   * テキストノードを1文字ずつの span 群へ置換
   * @param {Text} textNode
   * @param {{i:number}} counter
   */
  replaceTextNode(textNode, counter) {
    const text = textNode.textContent;
    if (!text) return;

    const frag = document.createDocumentFragment();

    // for...of でコードポイント単位（サロゲートペア対応）
    for (const ch of text) {
      const span = document.createElement('span');
      span.className = this.charClass;
      span.style.setProperty('--char-index', counter.i);
      // 空白文字（半角/NBSP/全角）はレイアウト維持のため目印クラスを付ける（CSSで white-space:pre）
      if (ch === ' ' || ch === ' ' || ch === '　') {
        span.classList.add(`${this.charClass}-space`);
      }
      span.textContent = ch;
      frag.appendChild(span);
      counter.i += 1;
    }

    textNode.replaceWith(frag);
  }
}
